import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import { auth, provider, signInWithPopup } from "../firebase/firebaseConfig";
import socket from "../socket";
import "./login.css";

const ROLE_ORDER = [
  "superadmin",
  "admin",
  "accounts",
  "hr",
  "academic_coordinator",
  "teacher",
  "student",
];

const BG_CANDIDATES = [
  `${process.env.PUBLIC_URL}/images/tpis_vijay_pur_background.png`,
  `${process.env.PUBLIC_URL}/images/SchooBackground.jpeg`,
  `${process.env.PUBLIC_URL}/image/SchooBackground.jpeg`,
];

function resolveFirstExistingImage(candidates) {
  return new Promise((resolve) => {
    let resolved = false;
    let remaining = candidates.length;

    if (!remaining) return resolve(null);

    candidates.forEach((src) => {
      const img = new Image();
      img.onload = () => {
        if (!resolved) {
          resolved = true;
          resolve(src);
        }
      };
      img.onerror = () => {
        remaining -= 1;
        if (remaining === 0 && !resolved) resolve(null);
      };
      img.src = src;
    });
  });
}

const joinRooms = (user, roles = []) => {
  const rl = roles.map((r) => (r || "").toLowerCase());

  if (rl.includes("student")) {
    socket.emit("joinRoom", { room: user.username });
    socket.emit("joinRoom", { room: "students" });
  }

  if (rl.includes("teacher") || rl.includes("academic_coordinator")) {
    socket.emit("joinRoom", { room: `teacher-${user.id}` });
    socket.emit("joinRoom", { room: "teachers" });
  }

  if (rl.includes("admin") || rl.includes("superadmin")) {
    socket.emit("joinRoom", { room: "admins" });
  }

  if (rl.includes("accounts")) {
    socket.emit("joinRoom", { room: "accounts" });
  }
};

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 533.5 544.3" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M533.5 278.4c0-17.4-1.6-34.1-4.7-50.2H272v95.1h147.1c-6.3 34-25 62.8-53.3 82v67h86.2c50.4-46.5 81.5-115 81.5-193.9z"
    />
    <path
      fill="#34A853"
      d="M272 544.3c72.3 0 132.9-23.9 177.2-65.1l-86.2-67c-24 16.1-54.6 25.7-91 25.7-69.9 0-129.1-47.2-150.3-110.7H33.7v69.6C77.8 490.3 168.8 544.3 272 544.3z"
    />
    <path
      fill="#4285F4"
      d="M121.7 327.2c-5.1-15.3-8-31.7-8-48.6s2.9-33.3 8-48.6V160.4H33.7C12.7 204.8 0 254.3 0 306.6c0 52.3 12.7 101.8 33.7 146.2l88-65.6z"
    />
    <path
      fill="#FBBC05"
      d="M272 107.7c39.2 0 74.5 13.5 102.2 39.9l76.7-76.7C404.8 26.2 344.2 0 272 0 168.8 0 77.8 54 33.7 160.4l88 69.6C142.9 154.9 202.1 107.7 272 107.7z"
    />
  </svg>
);

const inputStyle = {
  minHeight: "58px",
  borderRadius: "16px",
  border: "1px solid #d9e3f0",
  boxShadow: "none",
  paddingLeft: "16px",
  fontSize: "0.98rem",
};

const Login = () => {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [school, setSchool] = useState(null);
  const [bgUrl, setBgUrl] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [remember, setRemember] = useState(true);

  const navigate = useNavigate();
  const userInputRef = useRef(null);
  const apiBase = useMemo(
    () => process.env.REACT_APP_API_URL?.replace(/\/+$/, ""),
    []
  );

  useEffect(() => {
    userInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!apiBase) return;

    axios
      .get(`${apiBase}/schools`)
      .then((res) => {
        if (res.data?.length) setSchool(res.data[0]);
      })
      .catch(() => {});
  }, [apiBase]);

  useEffect(() => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }, []);

  useEffect(() => {
    (async () => {
      const found = await resolveFirstExistingImage(BG_CANDIDATES);
      setBgUrl(found);
    })();
  }, []);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err?.response?.status === 401) {
          delete axios.defaults.headers.common["Authorization"];
          localStorage.removeItem("token");
          localStorage.removeItem("roles");
          localStorage.removeItem("username");
          localStorage.removeItem("userId");
          localStorage.removeItem("name");
          localStorage.removeItem("activeRole");
          localStorage.removeItem("family");
          localStorage.removeItem("activeStudentAdmission");
          sessionStorage.removeItem("token");
          sessionStorage.removeItem("roles");
          window.dispatchEvent(new Event("user-logged-out"));
          navigate("/login", { replace: true });
        }
        return Promise.reject(err);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [navigate]);

  const afterAuth = async (data) => {
    const { token, user, roles } = data;
    const roleArr = Array.isArray(roles) ? roles : roles ? [roles] : [];
    const roleArrLower = roleArr.map((r) => (r || "").toLowerCase());

    try {
      if (remember) {
        localStorage.setItem("token", token);
        localStorage.setItem("roles", JSON.stringify(roleArr));
        localStorage.setItem("username", user.username);
        localStorage.setItem("userId", user.id);
        localStorage.setItem("name", user.name);
      } else {
        sessionStorage.setItem("token", token);
        sessionStorage.setItem("roles", JSON.stringify(roleArr));
        sessionStorage.setItem("username", user.username);
        sessionStorage.setItem("userId", user.id);
        sessionStorage.setItem("name", user.name);
      }

      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } catch (e) {
      console.warn("Storage failed", e);
    }

    try {
      if (data.family) {
        localStorage.setItem("family", JSON.stringify(data.family));
        localStorage.setItem(
          "activeStudentAdmission",
          data.family?.student?.admission_number || user.username
        );
      } else {
        localStorage.removeItem("family");
        localStorage.removeItem("activeStudentAdmission");
      }
      window.dispatchEvent(new Event("family-updated"));
    } catch (e) {
      console.warn("Failed to store family", e);
    }

    localStorage.removeItem("userRole");

    const defaultActive =
      ROLE_ORDER.find((r) => roleArrLower.includes(r)) ||
      roleArrLower[0] ||
      "";

    localStorage.setItem("activeRole", defaultActive);

    try {
      const fcm = window.FCMTOKEN;
      if (fcm) {
        axios
          .post(`${apiBase}/users/save-token`, {
            username: user.username,
            token: fcm,
          })
          .catch((e) => {
            console.warn("save-token failed", e?.response?.data || e.message);
          });
      }
    } catch (e) {
      console.warn("save-token call error", e);
    }

    try {
      if (token) {
        socket.auth = { token };
        if (socket.connected) socket.disconnect();
        socket.connect();
      }
    } catch (e) {
      console.warn("socket auth setup failed", e);
    }

    try {
      joinRooms(user, roleArrLower);
    } catch (e) {
      console.warn("joinRooms failed", e);
    }

    window.dispatchEvent(new Event("role-changed"));

    navigate(
      defaultActive === "accounts" ? "/accounts-dashboard" : "/dashboard",
      { replace: true }
    );
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const device = navigator.userAgent || "web";
      const { data } = await axios.post(`${apiBase}/users/login`, {
        login,
        password,
        device,
      });
      await afterAuth(data);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Invalid credentials";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError("");
    setLoading(true);

    try {
      const result = await signInWithPopup(auth, provider);
      const googleUser = result.user;
      const device = navigator.userAgent || "web";

      const { data } = await axios.post(`${apiBase}/users/login`, {
        google_id: googleUser.uid,
        google_email: googleUser.email,
        google_name: googleUser.displayName,
        google_username: googleUser.email,
        device,
      });

      await afterAuth(data);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Google login failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const fallbackLogo = `${process.env.PUBLIC_URL}/images/pts_logo.png`;
  const schoolLogoSrc = school?.logo ? `${apiBase}${school.logo}` : fallbackLogo;

  const schoolName = school?.name || "Pathseekers International School";
  const campusName = "Vijaypur Campus";
  const schoolTagline =
    school?.description ||
    "Unified access for academics, accounts, attendance, communication and school operations.";

  return (
    <div
      style={{
        minHeight: "100dvh",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#06111f",
        backgroundImage: bgUrl
          ? `linear-gradient(90deg, rgba(4, 13, 28, 0.84) 0%, rgba(4, 13, 28, 0.66) 38%, rgba(4, 13, 28, 0.82) 100%), url(${bgUrl})`
          : "linear-gradient(135deg, #08111f 0%, #0e2340 100%)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 12% 18%, rgba(255,255,255,0.16), transparent 18%), radial-gradient(circle at 88% 84%, rgba(77,142,255,0.16), transparent 20%)",
          pointerEvents: "none",
        }}
      />

      <div className="container position-relative" style={{ minHeight: "100dvh" }}>
        <div className="row align-items-center justify-content-center min-vh-100 py-4 py-lg-5">
          <div className="col-12 col-xl-11">
            <div
              className="overflow-hidden shadow-lg"
              style={{
                borderRadius: "30px",
                border: "1px solid rgba(255,255,255,0.14)",
                background: "rgba(255,255,255,0.08)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
              }}
            >
              <div className="row g-0">
                <div className="col-lg-6 d-none d-lg-block">
                  <div
                    style={{
                      minHeight: "780px",
                      height: "100%",
                      padding: "42px",
                      color: "#fff",
                      position: "relative",
                      background:
                        "linear-gradient(180deg, rgba(7, 18, 37, 0.30) 0%, rgba(7, 18, 37, 0.68) 38%, rgba(7, 18, 37, 0.88) 100%)",
                    }}
                  >
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "8px 14px",
                        borderRadius: "999px",
                        background: "rgba(255,255,255,0.12)",
                        border: "1px solid rgba(255,255,255,0.18)",
                        fontSize: "12px",
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        fontWeight: 700,
                        marginBottom: "26px",
                      }}
                    >
                      TPIS Branch ERP Portal
                    </div>

                    <div className="d-flex align-items-center gap-3 mb-4">
                      <div
                        style={{
                          width: 76,
                          height: 76,
                          borderRadius: "20px",
                          background: "rgba(255,255,255,0.14)",
                          border: "1px solid rgba(255,255,255,0.20)",
                          overflow: "hidden",
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                        }}
                      >
                        <img
                          src={schoolLogoSrc}
                          alt="School Logo"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            padding: "10px",
                          }}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = fallbackLogo;
                          }}
                        />
                      </div>

                      <div>
                        <div className="small text-white-50 mb-1">{campusName}</div>
                        <h3 className="mb-0 fw-bold">{schoolName}</h3>
                      </div>
                    </div>

                    <h1
                      className="fw-bold mb-3"
                      style={{
                        fontSize: "clamp(2.2rem, 3vw, 3.6rem)",
                        lineHeight: 1.08,
                        letterSpacing: "-0.03em",
                        maxWidth: "560px",
                      }}
                    >
                      Branch login experience for Vijaypur.
                    </h1>

                    <p
                      className="mb-4"
                      style={{
                        maxWidth: "540px",
                        fontSize: "1.04rem",
                        lineHeight: 1.9,
                        color: "rgba(255,255,255,0.86)",
                      }}
                    >
                      Access a cleaner, more readable school portal designed for day-to-day work across academics, accounts, teachers and administration.
                    </p>

                    <div className="row g-3 mt-1">
                      <div className="col-12">
                        <div
                          style={{
                            borderRadius: "22px",
                            padding: "20px 22px",
                            background: "rgba(255,255,255,0.10)",
                            border: "1px solid rgba(255,255,255,0.15)",
                          }}
                        >
                          <div className="small text-uppercase opacity-75 mb-2">
                            Portal Highlight
                          </div>
                          <div className="fw-semibold fs-5 mb-2">
                            Stronger readability over background image
                          </div>
                          <div className="small text-white-50" style={{ lineHeight: 1.8 }}>
                            The updated layout uses darker overlays and sharper card contrast so text remains visible even on detailed campus photography.
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div
                          style={{
                            height: "100%",
                            borderRadius: "20px",
                            padding: "18px",
                            background: "rgba(255,255,255,0.08)",
                            border: "1px solid rgba(255,255,255,0.12)",
                          }}
                        >
                          <div className="fw-semibold mb-2">Academic Workflows</div>
                          <div className="small text-white-50" style={{ lineHeight: 1.75 }}>
                            Attendance, results, daily teaching workflows and student records in one place.
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div
                          style={{
                            height: "100%",
                            borderRadius: "20px",
                            padding: "18px",
                            background: "rgba(255,255,255,0.08)",
                            border: "1px solid rgba(255,255,255,0.12)",
                          }}
                        >
                          <div className="fw-semibold mb-2">Operations & Accounts</div>
                          <div className="small text-white-50" style={{ lineHeight: 1.75 }}>
                            Fees, communication, reporting and branch administration with secure access control.
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      className="mt-4 pt-4"
                      style={{ borderTop: "1px solid rgba(255,255,255,0.14)" }}
                    >
                      <div className="small text-white-50 mb-2">Branch Note</div>
                      <div style={{ color: "rgba(255,255,255,0.90)", lineHeight: 1.8 }}>
                        {schoolTagline}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div
                    style={{
                      minHeight: "780px",
                      padding: "28px 22px",
                      background:
                        "linear-gradient(180deg, rgba(255,255,255,0.97) 0%, rgba(247,250,255,0.97) 100%)",
                    }}
                  >
                    <div
                      className="mx-auto d-flex flex-column justify-content-center h-100"
                      style={{ maxWidth: "470px" }}
                    >
                      <div className="d-lg-none text-center mb-4">
                        <img
                          src={schoolLogoSrc}
                          alt="School Logo"
                          style={{
                            width: 84,
                            height: 84,
                            objectFit: "contain",
                            marginBottom: 14,
                          }}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = fallbackLogo;
                          }}
                        />
                        <div
                          className="d-inline-block mb-2"
                          style={{
                            background: "#eef4ff",
                            color: "#194891",
                            borderRadius: "999px",
                            padding: "6px 12px",
                            fontSize: 12,
                            fontWeight: 700,
                            letterSpacing: "0.05em",
                            textTransform: "uppercase",
                          }}
                        >
                          {campusName}
                        </div>
                        <h3 className="fw-bold mb-1">{schoolName}</h3>
                        <p className="text-muted mb-0" style={{ lineHeight: 1.75 }}>
                          {schoolTagline}
                        </p>
                      </div>

                      <div
                        style={{
                          borderRadius: "28px",
                          border: "1px solid #e3ebf5",
                          background: "#ffffff",
                          boxShadow: "0 24px 60px rgba(18, 49, 95, 0.10)",
                          padding: "28px",
                        }}
                      >
                        <div
                          className="mb-3"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            background: "#eef4ff",
                            color: "#194891",
                            padding: "7px 12px",
                            borderRadius: "999px",
                            fontSize: "12px",
                            fontWeight: 700,
                            letterSpacing: "0.05em",
                            textTransform: "uppercase",
                          }}
                        >
                          Secure Access
                        </div>

                        <h2 className="fw-bold mb-2" style={{ color: "#10233f" }}>
                          Sign in to your portal
                        </h2>
                        <p className="text-muted mb-4" style={{ lineHeight: 1.8 }}>
                          Use your username or email to continue to the {campusName} dashboard.
                        </p>

                        {error && (
                          <div
                            className="mb-3"
                            style={{
                              borderRadius: "16px",
                              padding: "14px 16px",
                              background: "#fff2f2",
                              color: "#b42318",
                              border: "1px solid #ffd4d1",
                              fontSize: "0.95rem",
                            }}
                          >
                            {error}
                          </div>
                        )}

                        <form onSubmit={handleLogin} noValidate>
                          <div className="mb-3">
                            <label className="form-label fw-semibold" style={{ color: "#1a3358" }}>
                              Username or Email
                            </label>
                            <input
                              ref={userInputRef}
                              type="text"
                              className="form-control form-control-lg"
                              value={login}
                              onChange={(e) => setLogin(e.target.value)}
                              placeholder="Enter your username or email"
                              autoComplete="username"
                              required
                              style={inputStyle}
                            />
                          </div>

                          <div className="mb-2">
                            <label className="form-label fw-semibold" style={{ color: "#1a3358" }}>
                              Password
                            </label>
                            <div className="input-group input-group-lg">
                              <input
                                type={showPass ? "text" : "password"}
                                className="form-control"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter your password"
                                autoComplete="current-password"
                                required
                                style={{
                                  ...inputStyle,
                                  borderTopRightRadius: 0,
                                  borderBottomRightRadius: 0,
                                }}
                              />
                              <button
                                type="button"
                                className="btn"
                                onClick={() => setShowPass((s) => !s)}
                                aria-label={showPass ? "Hide password" : "Show password"}
                                style={{
                                  minWidth: "92px",
                                  borderTopRightRadius: "16px",
                                  borderBottomRightRadius: "16px",
                                  border: "1px solid #d9e3f0",
                                  borderLeft: "none",
                                  background: "#fff",
                                  color: "#194891",
                                  fontWeight: 700,
                                }}
                              >
                                {showPass ? "Hide" : "Show"}
                              </button>
                            </div>
                          </div>

                          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3 mb-4">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="rememberMe"
                                checked={remember}
                                onChange={(e) => setRemember(e.target.checked)}
                              />
                              <label className="form-check-label text-muted" htmlFor="rememberMe">
                                Keep me signed in
                              </label>
                            </div>

                            <button
                              type="button"
                              className="btn btn-link p-0 text-decoration-none"
                              onClick={() => navigate("/forgot-password")}
                              style={{ color: "#194891", fontWeight: 700 }}
                            >
                              Forgot password?
                            </button>
                          </div>

                          <button
                            type="submit"
                            className="btn w-100 mb-3"
                            disabled={loading}
                            style={{
                              minHeight: "58px",
                              borderRadius: "16px",
                              border: "none",
                              background: "linear-gradient(135deg, #0d4da8 0%, #1f6fe5 100%)",
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: "1rem",
                              boxShadow: "0 18px 40px rgba(31, 111, 229, 0.26)",
                            }}
                          >
                            {loading ? "Signing in..." : "Login to Dashboard"}
                          </button>

                          <div className="text-center text-muted my-3">or continue with</div>

                          <button
                            type="button"
                            className="btn w-100 d-flex align-items-center justify-content-center gap-2"
                            onClick={handleGoogleLogin}
                            disabled={loading}
                            style={{
                              minHeight: "56px",
                              borderRadius: "16px",
                              border: "1px solid #d9e3f0",
                              background: "#fff",
                              color: "#10233f",
                              fontWeight: 600,
                            }}
                          >
                            <GoogleIcon />
                            <span>{loading ? "Please wait..." : "Login with Google"}</span>
                          </button>
                        </form>
                      </div>

                      <div
                        className="mt-4"
                        style={{
                          borderRadius: "20px",
                          padding: "18px 18px",
                          background: "rgba(244, 248, 255, 0.96)",
                          border: "1px solid #e2ebf8",
                        }}
                      >
                       
                      </div>

                      <div className="mt-4 small text-muted" style={{ lineHeight: 1.8 }}>
                        By continuing you agree to our{" "}
                        <button
                          className="btn btn-link p-0 align-baseline text-decoration-none"
                          onClick={() => navigate("/terms")}
                          style={{ color: "#194891", fontWeight: 700 }}
                        >
                          Terms
                        </button>{" "}
                        and{" "}
                        <button
                          className="btn btn-link p-0 align-baseline text-decoration-none"
                          onClick={() => navigate("/privacy")}
                          style={{ color: "#194891", fontWeight: 700 }}
                        >
                          Privacy Policy
                        </button>
                        .
                      </div>

                      <div className="text-center mt-4 text-muted small">
                        © {new Date().getFullYear()} {schoolName} · {campusName}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mt-3 text-white-50 small d-lg-none">
              Vijaypur branch login redesign with improved readability.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;